import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, DatasetDefinition } from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";
import LivePanel from "../components/LivePanel.js";

function statusPill(s: DatasetDefinition["status"]) {
  if (s === "active")   return <span className="pill pill-ok">Active</span>;
  if (s === "archived") return <span className="pill pill-muted">Archived</span>;
  return <span className="pill pill-warn">Draft</span>;
}

function sensitivityPill(s: DatasetDefinition["sensitivity"]) {
  if (s === "restricted") return <span className="pill pill-err">Restricted</span>;
  if (s === "moderate")   return <span className="pill pill-warn">Moderate</span>;
  return <span className="pill pill-ok">Low</span>;
}

export default function Datasets() {
  const [rows, setRows] = useState<DatasetDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const t = useTableState(rows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["name", "code", "datasetType", "sourceDomain", "status", "sensitivity"],
    initialPageSize: 25
  });

  useEffect(() => {
    let cancelled = false;
    api.datasets()
      .then((d) => { if (!cancelled) setRows(d.datasets ?? []); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Datasets</h1>
          <p>Published analytical surfaces — warehouse, reporting, dashboard, publication, and portal datasets.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Inline live tail of data + aggregate events for this workspace.
          Lets the analyst watch ingestion + rollups as they happen
          without leaving the Datasets page. */}
      <LivePanel
        title="Live data &amp; aggregate events"
        categories={["trk-data", "agg-data", "aggregate"]}
        tailMax={15}
      />

      {loading ? <SkeletonTable rows={5} columns={4} /> :
       rows.length === 0 ? (
        <div className="empty">
          <p><strong>No datasets published yet.</strong></p>
          <p className="muted">
            Datasets expose a curated slice of the warehouse so portals and reports can query it.
            Run an ingestion from the <a href="/app/jobs">Jobs</a> page first, then publish a dataset here.
          </p>
        </div>
       ) : (
        <div className="table-scroll">
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            totalPages={t.totalPages} totalRows={t.totalRows}
            hideExport
            searchPlaceholder="Search datasets…"
          />
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Type</th>
                <th>Source</th>
                <th>Status</th>
                <th>Sensitivity</th>
                <th>Rows</th>
                <th>Last refresh</th>
                <th>Publication</th>
                <th>Forwarding</th>
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as DatasetDefinition[]).map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>{d.name}</strong>
                    {d.tags?.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {d.tags.map((t) => <span key={t} className="pill pill-muted" style={{ marginRight: 4 }}>{t}</span>)}
                      </div>
                    )}
                  </td>
                  <td><code>{d.code}</code></td>
                  <td>{d.datasetType.replace(/-/g, " ")}</td>
                  <td className="soft">{d.sourceDomain}</td>
                  <td>{statusPill(d.status)}</td>
                  <td>{sensitivityPill(d.sensitivity)}</td>
                  <td>{d.rowCount.toLocaleString()}</td>
                  <td className="soft">{d.lastRefreshedAt ? new Date(d.lastRefreshedAt).toLocaleString() : "—"}</td>
                  <td>{d.publicationEligible ? <span className="pill pill-ok">Eligible</span> : <span className="pill pill-muted">Internal</span>}</td>
                  <td>{d.forwardingEligible ? <span className="pill pill-ok">Eligible</span> : <span className="pill pill-muted">No</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}
    </div>
  );
}
